/**
 * AdminIntegrationsPage — Integrações por organização (Supabase org_integrations).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Package, Webhook, Settings, Loader2 } from 'lucide-react';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { supabase } from '../../../lib/supabaseClient';
import { useAppToast } from '../../../components/AppToast';
import type { IntegrationConfig } from '../../../types/adminConsole';
import {
  CATEGORY_LABELS,
  getCategories,
  mergeIntegrationsWithDb,
  type IntegrationKey,
} from '../integrations/integrationCatalog';
import { ToolIntegrationModal, WebhookConfigModal } from '../components/AdminIntegrationModals';

type ModalTarget =
  | { kind: 'webhooks'; integration: IntegrationConfig }
  | { kind: 'tool'; key: Exclude<IntegrationKey, 'webhooks'>; integration: IntegrationConfig };

export const AdminIntegrationsPage: React.FC<{ externalSelectedOrg?: string }> = ({ externalSelectedOrg }) => {
  const { showToast, ToastContainer } = useAppToast();
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>(() => mergeIntegrationsWithDb([]));
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalTarget | null>(null);

  useEffect(() => {
    if (externalSelectedOrg !== undefined) setSelectedOrgId(externalSelectedOrg);
  }, [externalSelectedOrg]);

  const loadIntegrations = useCallback(async (orgId: string) => {
    setLoading(true);
    try {
      const rows = await AdminConsoleService.getOrgIntegrations(orgId);
      setIntegrations(mergeIntegrationsWithDb(rows));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar integrações';
      showToast('error', msg);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (selectedOrgId) loadIntegrations(selectedOrgId);
  }, [selectedOrgId, loadIntegrations]);

  const persist = async (
    key: string,
    patch: Parameters<typeof AdminConsoleService.upsertOrgIntegration>[2],
  ) => {
    if (!selectedOrgId) return;
    const { data: { user } } = await supabase.auth.getUser();
    await AdminConsoleService.upsertOrgIntegration(selectedOrgId, key, patch, user?.id ?? '');
    await loadIntegrations(selectedOrgId);
  };

  const handleDisable = async (integration: IntegrationConfig) => {
    if (!selectedOrgId) return;
    setSavingKey(integration.key);
    try {
      await persist(integration.key, { enabled: false });
      showToast('success', `${integration.label} desconectado.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Falha ao desativar';
      showToast('error', msg);
    } finally {
      setSavingKey(null);
    }
  };

  const openConfigure = (integration: IntegrationConfig) => {
    if (integration.key === 'webhooks') {
      setModal({ kind: 'webhooks', integration });
      return;
    }
    setModal({
      kind: 'tool',
      key: integration.key as Exclude<IntegrationKey, 'webhooks'>,
      integration,
    });
  };

  const handleSwitchClick = (integration: IntegrationConfig) => {
    if (!selectedOrgId) {
      showToast('warning', 'Selecione um workspace no ribbon.');
      return;
    }
    if (integration.enabled) {
      handleDisable(integration);
      return;
    }
    openConfigure(integration);
  };

  const handleWebhookSave = async (payload: {
    webhook_url: string;
    webhook_secret: string;
    events: string[];
  }) => {
    if (!modal || modal.kind !== 'webhooks') return;
    setSavingKey('webhooks');
    try {
      await persist('webhooks', { enabled: true, ...payload });
      showToast('success', 'Webhooks configurados e conectados.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Falha ao salvar webhooks';
      showToast('error', msg);
      throw err;
    } finally {
      setSavingKey(null);
    }
  };

  const handleToolSave = async (payload: {
    webhook_url: string;
    webhook_secret: string;
    notes: string;
  }) => {
    if (!modal || modal.kind !== 'tool') return;
    setSavingKey(modal.key);
    try {
      await persist(modal.key, {
        enabled: true,
        webhook_url: payload.webhook_url || null,
        webhook_secret: payload.webhook_secret || null,
        config_json: { notes: payload.notes || '' },
      });
      showToast('success', `${modal.integration.label} conectado.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Falha ao salvar integração';
      showToast('error', msg);
      throw err;
    } finally {
      setSavingKey(null);
    }
  };

  const categories = getCategories();
  const isConnected = (i: IntegrationConfig) => i.status === 'connected';

  return (
    <div className="enterprise-page h-full overflow-y-auto">
      <ToastContainer />

      {loading && <ModuleLoadingState label="Carregando integrações" />}

      {!loading && selectedOrgId && categories.map((cat) => {
        const items = integrations.filter((i) => i.category === cat);
        return (
          <ModuleCard key={cat} title={CATEGORY_LABELS[cat] ?? cat}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {items.map((integration) => {
                const connected = isConnected(integration);
                const busy = savingKey === integration.key;
                return (
                  <div
                    key={integration.key}
                    className={`flex items-start justify-between gap-3 rounded border p-3 transition-colors ${
                      connected
                        ? 'border-emerald-300 bg-emerald-50/30 dark:border-emerald-700 dark:bg-emerald-900/10'
                        : 'border-[var(--border-default)]'
                    }`}
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-[var(--border-default)] bg-[var(--bg-surface)]">
                        {integration.key === 'webhooks' ? (
                          <Webhook size={14} className="text-[var(--text-secondary)]" />
                        ) : (
                          <Package size={14} className="text-[var(--text-secondary)]" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[12px] font-semibold text-[var(--text-primary)]">
                            {integration.label}
                          </span>
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                              connected
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'
                            }`}
                          >
                            {connected ? 'Conectado' : 'Desconectado'}
                          </span>
                        </div>
                        <p className="mt-0.5 text-[11px] leading-tight text-[var(--text-secondary)]">
                          {integration.description}
                        </p>
                        {integration.key === 'webhooks' && connected && integration.webhook_url && (
                          <p className="mt-1 max-w-[240px] truncate font-mono text-[10px] text-[var(--text-muted)]">
                            {integration.webhook_url}
                          </p>
                        )}
                        {integration.key === 'webhooks' && connected && integration.events && integration.events.length > 0 && (
                          <p className="mt-0.5 text-[10px] text-[var(--text-muted)]">
                            {integration.events.length} gatilho(s) ativo(s)
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        title="Configurar integração"
                        onClick={() => openConfigure(integration)}
                        className="rounded p-1.5 text-[var(--text-secondary)] transition-colors hover:bg-[var(--button-hover)] hover:text-[var(--brand-primary)]"
                      >
                        <Settings size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSwitchClick(integration)}
                        disabled={busy}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 ${
                          connected ? 'bg-[var(--brand-primary)]' : 'bg-[var(--border-strong,var(--border-default))]'
                        }`}
                        role="switch"
                        aria-checked={connected}
                      >
                        {busy ? (
                          <Loader2 size={10} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin text-white" />
                        ) : (
                          <span
                            className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                              connected ? 'translate-x-[18px]' : 'translate-x-0.5'
                            }`}
                          />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ModuleCard>
        );
      })}

      {!loading && !selectedOrgId && (
        <div className="mt-8 text-center text-[13px] text-[var(--text-secondary)]">
          Selecione um workspace no ribbon para gerenciar as integrações.
        </div>
      )}

      <div className="enterprise-card-surface mt-2 border-l-2 border-blue-400 p-3 text-[11px] text-[var(--text-secondary)]">
        Integrações são por organização. Switches persistem em <code className="font-mono">org_integrations</code>.
        Webhooks aceitam URL de destino e gatilhos operacionais; demais cards salvam credenciais para simulação e validação.
      </div>

      {modal?.kind === 'webhooks' && (
        <WebhookConfigModal
          integration={modal.integration}
          onSave={handleWebhookSave}
          onClose={() => setModal(null)}
        />
      )}

      {modal?.kind === 'tool' && (
        <ToolIntegrationModal
          integrationKey={modal.key}
          integration={modal.integration}
          onSave={handleToolSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};
